import React from 'react'
import { GetStaticPathsResult, InferGetStaticPropsType } from 'next'
import { Container } from '@scentregroup/ui'
import { useRouter } from 'next/router'

import { REVALIDATION_PERIODS } from '@scentregroup/shared/constants'
import { FeaturedLinksGrid } from '@scentregroup/shared/components/featured-links-grid'
import { storyClickTracker } from '@scentregroup/shared/pages/index.analytics'
import Layout from '@scentregroup/shared/hub-components/layout'
import { fetchNationalStaticData } from '@scentregroup/shared/hub-components/universal-header'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import { CoreContainer } from '@hub/core-container'
import { Pagination } from '../../../checkout/src/client/components/pagination'
import { makeApolloClient } from '../../lib/apolloClient'
import { Link } from '../../lib/react-router-dom-replacement'
import { getTheEditStories } from '../../lib/get-the-edit-stories'
import { GetStaticProps, GetStaticPropsContext } from '../../types/next'
import style from './index.scss'

const ITEMS_PER_PAGE = 12

function getPageNumber(page?: string): number {
  if (page) {
    try {
      return parseInt(page)
    } catch {
      return 1
    }
  }
  return 1
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: process.env.SKIP_STATIC_PATHS ? [] : [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({
  locale,
  params = {},
}: GetStaticPropsContext) => {
  const country = localeToCountry(locale)

  const client = makeApolloClient()

  const pageParam = Array.isArray(params.page) ? params.page[0] : params.page
  const page = getPageNumber(pageParam)

  const limit = page * ITEMS_PER_PAGE
  const skip = (page - 1) * ITEMS_PER_PAGE

  const [nationalHeaderData, storyPage] = await Promise.all([
    fetchNationalStaticData(client, { country }),
    getTheEditStories(client, country, skip, limit),
  ])

  return {
    props: {
      country,
      nationalHeaderData,
      storyPage: {
        ...storyPage,
        page,
      },
    },
    revalidate: REVALIDATION_PERIODS.static,
  }
}

const TheEdit = (props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { country, nationalHeaderData, storyPage } = props
  const { stories, total, page } = storyPage

  const totalPages = total / ITEMS_PER_PAGE

  const router = useRouter()

  const metaData = {
    title: `The Edit at Westfield ${country}`,
    description: `Westfield ${country} - home to your favourite editorials. Explore our range of articles, products, brands, events & offers`,
  }

  return (
    <Layout
      country={country}
      centre={undefined}
      header={{ variant: 'national', data: nationalHeaderData }}
      metaData={metaData}
      pageTitle="The Edit"
    >
      {stories.length > 0 && (
        <>
          <CoreContainer>
            <FeaturedLinksGrid
              title=""
              Link={Link}
              featuredLinks={stories}
              itemsPerRow={3}
              trackers={{ clicked: storyClickTracker }}
            />
            {totalPages > 1 && (
              <Container
                type="large"
                className={style.marketplacePaginationContainer}
              >
                <div />
                <Pagination
                  // The page here is 0 indexed
                  currentPage={page - 1}
                  totalPages={totalPages}
                  onChange={(page: number) =>
                    router.push(`/the-edit/${page + 1}`)
                  }
                />
              </Container>
            )}
          </CoreContainer>
        </>
      )}
    </Layout>
  )
}

const TheEditPage = (props: InferGetStaticPropsType<typeof getStaticProps>) => (
  <TheEdit {...props} />
)

export default TheEditPage
